import { map, mapObjIndexed, merge, prop, reduce, sortBy } from "ramda";
import type { DefaultRootState } from "react-redux";
import { createSelector } from "reselect";
import { sumDGItemCount } from "helpers/brand_menu/reconcilers";
import { findById, mostCommonBy } from "helpers/collections";

import { storeNamesSelector } from "selectors/context";
import type {
  IItem,
  IItemRecord,
  IMenu,
  IMenuRecord,
  IModGroup,
  IModGroupRecord,
  ISection,
  ISectionRecord,
  ITaxRate,
  ITaxRateCounted,
} from "types/brand_menu";

export const brandMenuSelector = (state: DefaultRootState) => state.brandMenu;

export const menuRecordsSelector = createSelector(
  brandMenuSelector,
  prop<"menus">("menus")
);
export const sectionRecordsSelector = createSelector(
  brandMenuSelector,
  prop<"sections">("sections")
);
export const itemRecordsSelector = createSelector(
  brandMenuSelector,
  prop<"items">("items")
);
export const categoriesSelector = createSelector(
  brandMenuSelector,
  prop<"categories">("categories")
);
export const coursesSelector = createSelector(
  brandMenuSelector,
  prop<"courses">("courses")
);
export const modifierGroupRecordsSelector = createSelector(
  brandMenuSelector,
  prop<"modifierGroups">("modifierGroups")
);
export const printerGroupsSelector = createSelector(
  brandMenuSelector,
  prop<"printerGroups">("printerGroups")
);
export const taxRatesSelector = createSelector(
  brandMenuSelector,
  prop<"taxRates">("taxRates")
);
export const requestStatusSelector = createSelector(
  brandMenuSelector,
  prop<"requestStatus">("requestStatus")
);

// HYDRATED RECORDS
export const sectionsSelector = createSelector(
  sectionRecordsSelector,
  storeNamesSelector,
  (sections, storeNames) =>
    mapObjIndexed<ISectionRecord[], ISection[]>((theseSections, storeId) => {
      const mergeStoreDetails = merge({
        storeId,
        storeName: storeNames[storeId],
      });
      return map<ISectionRecord, ISection>(mergeStoreDetails, theseSections);
    }, sections)
);

export const menusSelector = createSelector(
  menuRecordsSelector,
  sectionsSelector,
  storeNamesSelector,
  (menus, sectionsLookup, storeNames) =>
    mapObjIndexed<IMenuRecord[], IMenu[]>((theseMenus, storeId) => {
      const mergeStoreDetails = merge({
        storeId,
        storeName: storeNames[storeId],
      });

      return map<IMenuRecord, IMenu>((thisMenu) => {
        const sectionsAtThisRestaurant = sectionsLookup[storeId] || [];

        // map ISectionReference to the actual ISection record, if found
        const sections = reduce(
          (acc, { sectionId }) => {
            const match = findById(sectionId, sectionsAtThisRestaurant);
            if (match) {
              return acc.concat(match);
            }
            return acc;
          },
          [] as ISection[],
          sortBy(prop("order"), thisMenu.menuSections)
        );

        return mergeStoreDetails({
          ...thisMenu,
          itemsCount: sumDGItemCount(sections),
          sections,
        });
      }, theseMenus);
    }, menus)
);

export const itemsSelector = createSelector(
  itemRecordsSelector,
  storeNamesSelector,
  (items, storeNames) =>
    mapObjIndexed<IItemRecord[], IItem[]>((theseItems, storeId) => {
      const mergeStoreDetails = merge({
        storeId,
        storeName: storeNames[storeId],
      });
      return map<IItemRecord, IItem>(mergeStoreDetails, theseItems);
    }, items)
);

export const modifierGroupsSelector = createSelector(
  modifierGroupRecordsSelector,
  storeNamesSelector,
  (modifierGroups, storeNames) =>
    mapObjIndexed<IModGroupRecord[], IModGroup[]>((theseModGroups, storeId) => {
      const mergeStoreDetails = merge({
        storeId,
        storeName: storeNames[storeId],
      });
      return map<IModGroupRecord, IModGroup>(mergeStoreDetails, theseModGroups);
    }, modifierGroups)
);

export const taxRatesCountedSelector = createSelector(
  taxRatesSelector,
  itemsSelector,
  (taxRates, items) =>
    mapObjIndexed<ITaxRate[], ITaxRateCounted[]>((theseTaxRates, storeId) => {
      const itemsAtStore = items[storeId] || [];
      return theseTaxRates.map<ITaxRateCounted>((taxRate) => {
        const itemsHavingTaxRate = itemsAtStore.filter(
          ({ taxRates: itemTaxRates }) => itemTaxRates.includes(taxRate.id)
        );
        return merge(
          {
            count: itemsHavingTaxRate.length,
            label: `${taxRate.name} ${taxRate.displayPercent}`,
          },
          taxRate
        );
      });
    }, taxRates)
);

export const mostCommonCoursesSelector = createSelector(
  itemRecordsSelector,
  (items) =>
    // For each store location, find the most common course by counting items.
    mapObjIndexed<IItemRecord[], string>(
      (theseItems) => mostCommonBy(prop("courseId"), theseItems),
      items
    )
);
